/**
 * Telegram Mini App launch / invite helpers.
 */

import { BOT_USERNAME } from './game_config';
import { PUBLIC_ATELIER_ID, PUBLIC_WORKSHOP_ID } from '../game/config/tiers';

/** Raw start_param from Telegram WebApp or URL fallbacks. */
export function getTelegramStartParam() {
  if (typeof window === 'undefined') return '';
  const tg = window.Telegram?.WebApp;
  const fromTg = tg?.initDataUnsafe?.start_param;
  if (fromTg) return String(fromTg);

  const q = new URLSearchParams(window.location.search);
  const fromQuery = q.get('tgWebAppStartParam') || q.get('startapp');
  if (fromQuery) return fromQuery;

  const hash = window.location.hash?.replace(/^#/, '') || '';
  if (hash) {
    const hq = new URLSearchParams(hash);
    const fromHash = hq.get('tgWebAppStartParam') || hq.get('startapp');
    if (fromHash) return fromHash;
  }
  return '';
}

/**
 * Parse `syndicate_<uuid>` start params.
 * Legacy `syndicate_invite` returns null (no id).
 */
export function parseSyndicateInviteId(startParam) {
  const raw = String(startParam || '').trim();
  if (!raw.startsWith('syndicate_')) return null;
  const id = raw.slice('syndicate_'.length);
  if (!id || id === 'invite') return null;
  return id;
}

/**
 * Public floor deep links: `public_workshop` | `public_atelier` | `play`.
 * @returns {{ kind: 'workshop'|'atelier'|'play' } | null}
 */
export function parsePublicFloorStart(startParam) {
  const raw = String(startParam || '')
    .trim()
    .toLowerCase();
  if (raw === 'public_workshop' || raw === 'workshop') return { kind: 'workshop' };
  if (raw === 'public_atelier' || raw === 'atelier') return { kind: 'atelier' };
  if (raw === 'play') return { kind: 'play' };
  return null;
}

export function buildSyndicateInviteLink(syndicateId) {
  const bot = BOT_USERNAME || 'voxelquest_bot';
  const id = String(syndicateId || '').trim();
  if (!id || id === 'play') {
    return `https://t.me/${bot}?startapp=play`;
  }
  if (id === PUBLIC_WORKSHOP_ID) {
    return `https://t.me/${bot}?startapp=public_workshop`;
  }
  if (id === PUBLIC_ATELIER_ID) {
    return `https://t.me/${bot}?startapp=public_atelier`;
  }
  return `https://t.me/${bot}?startapp=syndicate_${encodeURIComponent(id)}`;
}

export function buildSyndicateInviteMessage(syndicateId, syndicateName) {
  const link = buildSyndicateInviteLink(syndicateId);
  const id = String(syndicateId || '').trim();
  let name = String(syndicateName || 'my syndicate').trim() || 'my syndicate';
  let text;
  if (id === PUBLIC_WORKSHOP_ID) {
    text = `Join me on Voxel Quest Public Workshop (8×8)!\nTap to open:`;
  } else if (id === PUBLIC_ATELIER_ID) {
    text = `Join me on Voxel Quest Public Atelier (16×16)!\nTap to open:`;
  } else {
    text = `Join my Voxel Quest syndicate “${name}”!\nTap to open:`;
  }
  return { link, text, name };
}

/**
 * Open Telegram’s native share picker with invite link + message.
 * Falls back to clipboard.
 * @returns {'shared'|'copied'|'failed'}
 */
export async function shareSyndicateInvite(syndicateId, syndicateName) {
  const { link, text } = buildSyndicateInviteMessage(syndicateId, syndicateName);
  const tg = window.Telegram?.WebApp;

  try {
    if (typeof tg?.shareURL === 'function') {
      tg.shareURL(link, text);
      return 'shared';
    }
  } catch {
    /* fall through */
  }

  const shareDeepLink =
    `https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(text)}`;

  try {
    if (typeof tg?.openTelegramLink === 'function') {
      tg.openTelegramLink(shareDeepLink);
      return 'shared';
    }
  } catch {
    /* fall through */
  }

  try {
    if (navigator.share) {
      await navigator.share({ title: 'Voxel Quest', text: `${text}\n${link}`, url: link });
      return 'shared';
    }
  } catch {
    /* user cancelled or unsupported */
  }

  const ok = await copyText(`${text}\n${link}`);
  return ok ? 'copied' : 'failed';
}

export async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}
